// REST client for ConversationReviewService, the per-trace review reads, plus
// the wire adapters that turn grpc-gateway JSON into the shapes the dashboard
// renders. Generic on purpose: the alerts page is the first consumer and the
// trace panel's own review migration is expected to reuse it.
//
// Ids. Analytics surfaces identify sessions by hyphenated uuid; the review
// routes take the same ids as protobuf bytes (base64). This file is a sanctioned
// conversion boundary (dashboard/AGENTS.md, "IDs on the wire"); the alert
// triage client borrows these two helpers rather than adding its own.
import { idForPath, restGet, restPost } from "./restClient.js";

// Hyphenated uuid -> standard base64 of its 16 raw bytes. Null for anything
// that is not a uuid.
export function uuidToBytesBase64(uuid) {
    const hex = String(uuid || "").replace(/-/g, "").toLowerCase();
    if (!/^[0-9a-f]{32}$/.test(hex)) return null;
    let bin = "";
    for (let i = 0; i < 32; i += 2) bin += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
    return btoa(bin);
}

// Base64 protobuf bytes -> hyphenated uuid. Null for anything that is not 16
// bytes.
export function bytesBase64ToUuid(b64) {
    if (b64 == null || b64 === "") return null;
    let bin;
    try {
        bin = atob(String(b64).replace(/-/g, "+").replace(/_/g, "/"));
    } catch {
        return null;
    }
    if (bin.length !== 16) return null;
    let hex = "";
    for (let i = 0; i < 16; i += 1) hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function pathId(uuid, field) {
    const b64 = uuidToBytesBase64(uuid);
    if (!b64) throw new Error(`${field} is not a uuid`);
    return idForPath(b64);
}

// grpc-gateway emits camelCase; keep the snake fallback so a gateway flag flip
// does not blank every field.
export function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

// "BEHAVIOUR_SEVERITY_HIGH" -> "high"; "UNSPECIFIED" -> "". Numbers (a client
// without the enum table) come back as-is in string form.
export function enumSuffix(prefix, v) {
    if (v == null) return "";
    const s = String(v);
    const tail = s.startsWith(prefix) ? s.slice(prefix.length) : s;
    const lower = tail.toLowerCase();
    return lower === "unspecified" ? "" : lower;
}

export function parseTimestampMs(v) {
    if (v == null || v === "") return null;
    const ms = Date.parse(String(v));
    return Number.isFinite(ms) ? ms : null;
}

// ── Routes ─────────────────────────────────────────────────────────────

export async function traceReviewState(traceId) {
    return restGet(`/v1/conversation-traces/${pathId(traceId, "traceId")}/review-state`);
}

export async function listTraceReviewReports(traceId, { pageSize = 25, pageToken = "" } = {}) {
    const json = await restGet(`/v1/conversation-traces/${pathId(traceId, "traceId")}/review-reports`, {
        page_size: pageSize,
        page_token: pageToken,
    });
    return {
        reports: (pick(json, "reports", "reports") ?? []).map(adaptReportHeader),
        nextPageToken: pick(json, "next_page_token", "nextPageToken") ?? "",
    };
}

export async function listTraceReviewFindings(traceId, { pageSize = 50, pageToken = "" } = {}) {
    const json = await restGet(`/v1/conversation-traces/${pathId(traceId, "traceId")}/review-findings`, {
        page_size: pageSize,
        page_token: pageToken,
    });
    const rows = pick(json, "findings", "findings") ?? [];
    return {
        findings: rows.map((row) => ({
            ...adaptFinding(pick(row, "finding", "finding") ?? row),
            reportId: bytesBase64ToUuid(pick(row, "report_id", "reportId")),
            targetSetHash: pick(row, "target_set_hash", "targetSetHash") ?? "",
        })),
        nextPageToken: pick(json, "next_page_token", "nextPageToken") ?? "",
    };
}

export async function getReviewReport(reportId, { signal } = {}) {
    return restGet(`/v1/conversation-review-reports/${pathId(reportId, "reportId")}`, null, { signal });
}

// Analytics text is clipped to 512 characters at publication. Explicit detail
// expansion uses the original report to preserve text and citation coordinates.
export async function loadReviewFindingDetails({ traceId, findings, signal, getReport = getReviewReport }) {
    if (!findings.length) return [];
    if (!uuidToBytesBase64(traceId) || findings.length > 50
        || findings.some((f) => !uuidToBytesBase64(f.findingId) || !uuidToBytesBase64(f.reportId))) {
        throw new Error("Finding detail requires a Prompt Session and up to 50 identified findings.");
    }
    const reportIds = [...new Set(findings.map((f) => f.reportId))];
    const details = new Map();
    for (let offset = 0; offset < reportIds.length; offset += 2) {
        signal?.throwIfAborted();
        const reports = await Promise.all(reportIds.slice(offset, offset + 2).map(async (reportId) => {
            const report = await getReport(reportId, { signal });
            signal?.throwIfAborted();
            if (bytesBase64ToUuid(pick(report, "trace_id", "traceId")) !== traceId
                || bytesBase64ToUuid(pick(report.header, "report_id", "reportId")) !== reportId) {
                throw new Error("The report did not match the selected finding. Refresh to try again.");
            }
            return { reportId, report };
        }));
        for (const { reportId, report } of reports) {
            const selected = findings.filter((f) => f.reportId === reportId);
            const expired = Boolean(pick(report, "document_expired", "documentExpired"));
            for (const base of selected) {
                const matches = (report.findings ?? []).filter((f) => bytesBase64ToUuid(pick(f, "finding_id", "findingId")) === base.findingId);
                if (expired) {
                    details.set(base.findingId, { ...base, documentExpired: true, rationale: "", citations: [] });
                } else {
                    if (matches.length !== 1) throw new Error("This finding is not available in its original report. Refresh to try again.");
                    const finding = adaptFinding(matches[0]);
                    details.set(base.findingId, { ...base, ...finding, citationMetadataUnavailable: finding.citations.some((c) => !c.source) });
                }
            }
        }
    }
    return findings.map((f) => details.get(f.findingId));
}

export async function getReviewProfile({ scope = null, signal } = {}) {
    const rawKind = String(scope?.kind || "").trim();
    const scopeKind = rawKind
        ? (rawKind.startsWith("REVIEW_SCOPE_KIND_") ? rawKind : `REVIEW_SCOPE_KIND_${rawKind.toUpperCase()}`)
        : "REVIEW_SCOPE_KIND_DEPARTMENT";
    const params = scope?.key ? {
        "scope.kind": scopeKind,
        "scope.key": scope.key,
    } : null;
    return adaptProfile(await restGet("/v1/review-profile", params, { signal }));
}

/// Serve a completed work-register build (censusClient.listRegisterBuilds) as
/// the tenant review profile: the build's org document becomes the discovered
/// norms and intents at version expectedVersion + 1, through the same
/// versioned write the discovery agent uses. A stale expectedVersion rejects
/// with a conflict -- re-read the profile and try again. Resolves the profile.
export async function serveRegisterBuild({ buildId, expectedVersion, signal } = {}) {
    const wire = await restPost(
        "/v1/review-profile:serve-register-build",
        { buildId, expectedVersion: Number(expectedVersion || 0) },
        { signal },
    );
    return adaptProfile(wire);
}

export async function getReviewAvailability({ signal } = {}) {
    const json = await restGet("/v1/conversation-review-configurations", null, { signal });
    return (pick(json, "configurations", "configurations") ?? []).length > 0;
}

// ── Adapters ───────────────────────────────────────────────────────────

function adaptClaim(wire) {
    if (!wire) return { result: "", reasoning: "" };
    return {
        result: enumSuffix("VERIFIER_RESULT_", pick(wire, "result", "result")),
        reasoning: pick(wire, "reasoning", "reasoning") ?? "",
    };
}

function adaptFrame(wire, prefix) {
    if (!wire) return { status: "", reference: "", note: "", claim: adaptClaim(null) };
    return {
        status: enumSuffix(prefix, pick(wire, "status", "status")),
        reference: pick(wire, "reference", "reference") ?? "",
        note: pick(wire, "note", "note") ?? "",
        claim: adaptClaim(pick(wire, "claim", "claim")),
    };
}

function adaptCitation(wire) {
    const source = pick(wire, "source", "source");
    return {
        itemRef: pick(wire, "item_ref", "itemRef") ?? "",
        status: enumSuffix("CITATION_STATUS_", pick(wire, "status", "status")),
        purpose: enumSuffix("CITATION_PURPOSE_", pick(wire, "purpose", "purpose")),
        quote: pick(wire, "quote", "quote") ?? "",
        source: source
            ? {
                messageId: bytesBase64ToUuid(pick(source, "message_id", "messageId")),
                representationId: bytesBase64ToUuid(pick(source, "representation_id", "representationId")),
                sourceEventId: bytesBase64ToUuid(pick(source, "source_event_id", "sourceEventId")),
                partOrdinal: Number(pick(source, "part_ordinal", "partOrdinal") ?? 0),
                start: Number(pick(source, "start", "start") ?? 0),
                end: Number(pick(source, "end", "end") ?? 0),
            }
            : null,
    };
}

export function adaptFinding(wire) {
    const behaviour = pick(wire, "behaviour", "behaviour") ?? {};
    const content = pick(wire, "content", "content") ?? {};
    return {
        findingId: bytesBase64ToUuid(pick(wire, "finding_id", "findingId")),
        ordinal: Number(pick(wire, "ordinal", "ordinal") ?? 0),
        title: pick(wire, "title", "title") ?? "",
        rationale: pick(wire, "rationale", "rationale") ?? "",
        reason: enumSuffix("ACTIONABLE_REASON_", pick(wire, "reason", "reason")),
        actionable: Boolean(pick(wire, "actionable", "actionable")),
        evidenceStatus: enumSuffix("EVIDENCE_STATUS_", pick(wire, "evidence_status", "evidenceStatus")),
        behaviourSeverity: enumSuffix("BEHAVIOUR_SEVERITY_", pick(behaviour, "severity", "severity")),
        behaviourCategory: pick(behaviour, "category", "category") ?? "",
        behaviourDefinition: pick(behaviour, "definition", "definition") ?? "",
        contentSensitivity: enumSuffix("CONTENT_SENSITIVITY_", pick(content, "sensitivity", "sensitivity")),
        contentCategory: pick(content, "category", "category") ?? "",
        contentDefinition: pick(content, "definition", "definition") ?? "",
        policy: adaptFrame(pick(wire, "policy", "policy"), "POLICY_STATUS_"),
        norms: adaptFrame(pick(wire, "norms", "norms"), "NORM_STATUS_"),
        intent: adaptFrame(pick(wire, "intent", "intent"), "INTENT_STATUS_"),
        citations: (pick(wire, "citations", "citations") ?? []).map(adaptCitation),
        verifierStatus: enumSuffix("VERIFIER_STATUS_", pick(wire, "verifier_status", "verifierStatus")),
        behaviourClaim: adaptClaim(pick(wire, "behaviour_claim", "behaviourClaim")),
        estimates: adaptEstimates(pick(wire, "estimates", "estimates")),
    };
}

// What strong reviewers would say about the finding, read off the judge's own
// state while it wrote it. Null when the report carries none; a value is null
// when the probe has no head for it.
function adaptEstimates(wire) {
    if (!wire) return null;
    const value = (raw) => (raw == null || !Number.isFinite(Number(raw)) ? null : Number(raw));
    return {
        version: pick(wire, "version", "version") ?? "",
        readerConfidence: value(pick(wire, "reader_confidence", "readerConfidence")),
        independentConfidence: value(pick(wire, "independent_confidence", "independentConfidence")),
        readerSeverity: value(pick(wire, "reader_severity", "readerSeverity")),
        receivedConduct: value(pick(wire, "received_conduct", "receivedConduct")),
    };
}

export function adaptReportHeader(wire) {
    const scope = pick(wire, "scope", "scope");
    return {
        reportId: bytesBase64ToUuid(pick(wire, "report_id", "reportId")),
        taskId: bytesBase64ToUuid(pick(wire, "task_id", "taskId")),
        isPartial: Boolean(pick(wire, "is_partial", "isPartial")),
        establishesCoverage: Boolean(pick(wire, "establishes_coverage", "establishesCoverage")),
        preferred: Boolean(pick(wire, "preferred", "preferred")),
        worstBehaviour: enumSuffix("BEHAVIOUR_SEVERITY_", pick(wire, "worst_behaviour_severity", "worstBehaviourSeverity")),
        worstContent: enumSuffix("CONTENT_SENSITIVITY_", pick(wire, "worst_content_sensitivity", "worstContentSensitivity")),
        findingCount: Number(pick(wire, "finding_count", "findingCount") ?? 0),
        actionableFindingCount: Number(pick(wire, "actionable_finding_count", "actionableFindingCount") ?? 0),
        readerMode: enumSuffix("READER_MODE_", pick(wire, "reader_mode", "readerMode")),
        modelConfigurationId: pick(wire, "model_configuration_id", "modelConfigurationId") ?? "",
        profileVersion: Number(pick(wire, "profile_version", "profileVersion") ?? 0),
        scope: scope ? {
            kind: enumSuffix("REVIEW_SCOPE_KIND_", pick(scope, "kind", "kind")),
            key: pick(scope, "key", "key") ?? "",
        } : null,
        scopeProfileVersion: Number(pick(wire, "scope_profile_version", "scopeProfileVersion") ?? 0),
        completedAtMs: parseTimestampMs(pick(wire, "completed_at", "completedAt")),
    };
}

function adaptProfileItem(wire) {
    return {
        reference: pick(wire, "reference", "reference") ?? "",
        title: pick(wire, "title", "title") ?? "",
        description: pick(wire, "description", "description") ?? "",
        basis: pick(wire, "basis", "basis") ?? "",
        prevalence: enumSuffix("NORM_PREVALENCE_", pick(wire, "prevalence", "prevalence")),
        weight: enumSuffix("INTENT_WEIGHT_", pick(wire, "weight", "weight")),
    };
}

export function adaptProfile(wire) {
    const scope = pick(wire, "scope", "scope");
    return {
        version: Number(pick(wire, "version", "version") ?? 0),
        policyText: pick(wire, "policy_text", "policyText") ?? "",
        discoverySummary: pick(wire, "discovery_summary", "discoverySummary") ?? "",
        norms: (pick(wire, "norms", "norms") ?? []).map(adaptProfileItem),
        intents: (pick(wire, "intents", "intents") ?? []).map(adaptProfileItem),
        scope: scope ? {
            kind: enumSuffix("REVIEW_SCOPE_KIND_", pick(scope, "kind", "kind")),
            key: pick(scope, "key", "key") ?? "",
        } : null,
    };
}
